import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useLayoutEffect,
} from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardText,
  Badge,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import moment from "moment";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);
const num = (v) => new Intl.NumberFormat("en-US").format(v || 0);
const colorUtil = (v) => ({ color: v < 0 ? "#c62828" : "#2e7d32", fontWeight: "bold" });

function ReporteVentasPlataforma() {
  const { user } = useContext(AuthContext);
  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;

  // El cliente puede ver costos/utilidad solo si tiene el permiso
  const puedeVerCostos = user.inventarios_costos;

  const [comments, setComments] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

  let hoy = new Date();
  let endDate = moment(hoy).format("YYYY-MM-DD");
  let startDate = moment(hoy).format("YYYY-MM-DD");

  const [lineas, setLineas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedLinea, setSelectedLinea] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [cargando, setCargando] = useState(true);

  const Toast = Swal.mixin({
    toast: true,
    position: "center",
    showConfirmButton: false,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
  });

  if (cargando) {
    Toast.fire({
      html: `<i class="fas fa-hourglass-half custom-spin fa-2x" style="margin-right: 16px;"></i>`,
      title: "Danos unos segundos....",
      customClass: { icon: "animated-icon" },
    });
  }

  function BuscaInfo() {
    setComments([]);
    setResumen([]);
    setSelectedArea("");
    setSelectedLinea("");
    setCargando(true);
    axios
      .get(
        `${URL_DASHBOARDS}VentasPlataforma/${selectedFechaInicio}/${selectedFechaFin}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        let allData = response.data;
        setComments(allData);

        let unicosLinea = allData.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.idLinea === value.idLinea)
        );
        setLineas(unicosLinea);

        let unicosArea = allData.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.idArea === value.idArea)
        );
        setAreas(unicosArea);

        setCargando(false);
        Toast.close();
      })
      .catch((err) => {
        console.log(err);
      });
  }

  useEffect(() => {
    BuscaInfo();
  }, []);

  // Resumen compacto por plataforma (alimenta tabla resumen + grafica)
  useEffect(() => {
    let filtrado = comments;
    if (selectedLinea) {
      filtrado = filtrado.filter((e) => e.idLinea === selectedLinea);
    }
    if (selectedArea) {
      filtrado = filtrado.filter((e) => e.idArea === selectedArea);
    }

    let agrupado = filtrado.reduce(function (groups, item) {
      const val = item.idArea;
      groups[val] = groups[val] || {
        Area: item.Area,
        idArea: item.idArea,
        Cantidad: 0,
        Venta: 0,
        Costo: 0,
        Utilidad: 0,
      };
      groups[val].Cantidad += parseFloat(item.Cantidad) || 0;
      groups[val].Venta += parseFloat(item.Venta) || 0;
      groups[val].Costo += parseFloat(item.Costo) || 0;
      groups[val].Utilidad += parseFloat(item.Utilidad) || 0;
      return groups;
    }, []);

    let res = Object.values(agrupado).map((r) => ({
      ...r,
      PorcentajeUtilidad: r.Venta > 0 ? (r.Utilidad / r.Venta) * 100 : 0,
    }));

    setResumen(res);
  }, [comments, selectedArea, selectedLinea]);

  // Totales para las tarjetas KPI
  const totales = useMemo(() => {
    const t = resumen.reduce(
      (acc, r) => {
        acc.cantidad += r.Cantidad || 0;
        acc.venta += r.Venta || 0;
        acc.utilidad += r.Utilidad || 0;
        return acc;
      },
      { cantidad: 0, venta: 0, utilidad: 0 }
    );
    t.margen = t.venta > 0 ? (t.utilidad / t.venta) * 100 : 0;
    return t;
  }, [resumen]);

  // Grafica de barras: Venta y Utilidad por plataforma
  useLayoutEffect(() => {
    if (!resumen.length) return;
    let root = am5.Root.new("chartdivPlataforma");
    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
      })
    );

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "Area",
        renderer: am5xy.AxisRendererX.new(root, { cellStartLocation: 0.1, cellEndLocation: 0.9 }),
      })
    );
    xAxis.data.setAll(resumen);

    function creaSerie(name, field, color) {
      let serie = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: field,
          categoryXField: "Area",
          tooltip: am5.Tooltip.new(root, { labelText: "{name}: ${valueY}" }),
        })
      );
      serie.columns.template.setAll({
        width: am5.percent(90),
        tooltipY: 0,
        fill: am5.color(color),
        stroke: am5.color(color),
      });
      serie.data.setAll(resumen);
      serie.appear(1000);
      return serie;
    }

    creaSerie("Venta", "Venta", 0x1565c0);
    if (puedeVerCostos) {
      creaSerie("Utilidad", "Utilidad", 0x2e7d32);
    }

    let legend = chart.children.push(am5.Legend.new(root, {}));
    legend.data.setAll(chart.series.values);

    chart.set("cursor", am5xy.XYCursor.new(root, {}));
    am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
    });

    return () => {
      root.dispose();
    };
  }, [resumen, puedeVerCostos]);

  function excel() {
    const dataExcel = comments.map((a) => {
      if (
        (selectedArea == 0 || selectedArea == a.idArea) &&
        (selectedLinea == 0 || selectedLinea == a.idLinea)
      ) {
        let fila = {
          Codigo: a.Codigo,
          Nombre: a.Nombre,
          Plataforma: a.Area,
          Categoria: a.Linea,
          Cantidad: a.Cantidad,
          Venta: a.Venta,
        };
        if (puedeVerCostos) {
          fila.Costo = a.Costo;
          fila.Utilidad = a.Utilidad;
          fila.PorcentajeUtilidad = (a.PorcentajeUtilidad * 100).toFixed(2);
        }
        return fila;
      }
    });
    const dataExportExcel = Object.values(dataExcel).filter((el) => el != null);
    let sheetFilter = ["Codigo", "Nombre", "Plataforma", "Categoria", "Cantidad", "Venta"];
    if (puedeVerCostos) sheetFilter = sheetFilter.concat(["Costo", "Utilidad", "PorcentajeUtilidad"]);

    // Hoja resumen por plataforma (lo mismo que se muestra arriba)
    let resumenFilter = ["Plataforma", "Cantidad", "Venta"];
    if (puedeVerCostos) resumenFilter = resumenFilter.concat(["Utilidad", "PorcentajeUtilidad"]);
    const resumenData = resumen.map((r) => {
      const fila = { Plataforma: r.Area, Cantidad: r.Cantidad, Venta: r.Venta };
      if (puedeVerCostos) {
        fila.Utilidad = r.Utilidad;
        fila.PorcentajeUtilidad = r.PorcentajeUtilidad.toFixed(2);
      }
      return fila;
    });

    var option = {};
    option.fileName = "ReporteVentasPlataforma";
    option.datas = [
      {
        sheetData: resumenData,
        sheetName: "Resumen por Plataforma",
        sheetFilter: resumenFilter,
        sheetHeader: resumenFilter,
      },
      {
        sheetData: dataExportExcel,
        sheetName: "Detalle por Producto",
        sheetFilter: sheetFilter,
        sheetHeader: sheetFilter,
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  function PDFTabla() {
    const data = comments.map((a) => {
      if (
        (selectedArea == 0 || selectedArea == a.idArea) &&
        (selectedLinea == 0 || selectedLinea == a.idLinea)
      ) {
        let fila = [
          a.Codigo,
          a.Nombre,
          a.Area,
          new Intl.NumberFormat("en-US").format(a.Cantidad),
          new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(a.Venta),
        ];
        if (puedeVerCostos) {
          fila.push(
            new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(a.Utilidad)
          );
          fila.push(`${(a.PorcentajeUtilidad * 100).toFixed(1)}%`);
        }
        return fila;
      }
    });
    const dataPDFLimpia = Object.values(data).filter((el) => el != null);
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 120, 15, 45, 15);
    doc.text(`Reporte de Ventas por Plataforma`, 20, 30);
    doc.text(`${selectedFechaInicio} - ${selectedFechaFin}`, 20, 40);

    // Resumen por plataforma (lo mismo que se muestra arriba)
    let resHead = ["Plataforma", "Cantidad", "Venta"];
    if (puedeVerCostos) resHead = resHead.concat(["Utilidad", "% Utilidad"]);
    const resBody = resumen.map((r) => {
      const fila = [
        r.Area,
        new Intl.NumberFormat("en-US").format(r.Cantidad),
        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(r.Venta),
      ];
      if (puedeVerCostos) {
        fila.push(new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(r.Utilidad));
        fila.push(`${r.PorcentajeUtilidad.toFixed(1)}%`);
      }
      return fila;
    });
    doc.autoTable({ head: [resHead], body: resBody, startY: 45, styles: { fontSize: 9 } });

    let head = ["Codigo", "Nombre", "Plataforma", "Cantidad", "Venta"];
    if (puedeVerCostos) head = head.concat(["Utilidad", "% Utilidad"]);
    doc.autoTable({ head: [head], body: dataPDFLimpia, startY: doc.lastAutoTable.finalY + 8, styles: { fontSize: 7 } });
    doc.save(`ReporteVentasPlataforma.pdf`);
  }

  const headers = [
    { name: "Codigo", field: "Codigo", sortable: true },
    { name: "Nombre", field: "Nombre", sortable: true },
    { name: "Plataforma", field: "Area", sortable: true },
    { name: "Categoria", field: "Linea", sortable: true },
    { name: "Cantidad", field: "Cantidad", sortable: true },
    { name: "Venta", field: "Venta", sortable: true },
  ];
  if (puedeVerCostos) {
    headers.push({ name: "Utilidad", field: "Utilidad", sortable: true });
    headers.push({ name: "% Utilidad", field: "PorcentajeUtilidad", sortable: true });
  }

  const commentsData = useMemo(() => {
    let computedComments = comments;

    if (search) {
      computedComments = computedComments.filter(
        (comment) =>
          (comment.Codigo || "").toLowerCase().includes(search.toLowerCase()) ||
          (comment.Nombre || "").toLowerCase().includes(search.toLowerCase()) ||
          (comment.Area || "").toLowerCase().includes(search.toLowerCase())
      );
    }
    if (selectedLinea) {
      computedComments = computedComments.filter((e) => e.idLinea === selectedLinea);
    }
    if (selectedArea) {
      computedComments = computedComments.filter((e) => e.idArea === selectedArea);
    }

    setTotalItems(computedComments.length);

    const numFields = ["Cantidad", "Venta", "Utilidad", "PorcentajeUtilidad"];
    if (sorting.field && !numFields.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computedComments = [...computedComments].sort(
        (a, b) => reversed * (a[sorting.field] || "").localeCompare(b[sorting.field] || "")
      );
    }
    if (sorting.field && numFields.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computedComments = [...computedComments].sort(
        (a, b) => reversed * (parseFloat(a[sorting.field]) - parseFloat(b[sorting.field]))
      );
    }

    return computedComments.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting, selectedArea, selectedLinea]);

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_reportes ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <h3>Reporte de Ventas por Plataforma</h3>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <h4></h4>
              <Button size="sm" className="btn" id="botonListado" onClick={BuscaInfo} color="info">
                Actualizar
              </Button>
            </Col>
          </Row>
          <br />
          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Categoria</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedLinea}
                onChange={(e) => setSelectedLinea(e.target.value)}
              >
                <option value="">Selecciona</option>
                {lineas
                  .sort((a, b) => (a.Linea > b.Linea ? 1 : -1))
                  .map((a) => (
                    <option value={a.idLinea}>{a.Linea}</option>
                  ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Plataforma</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
              >
                <option value="">Selecciona</option>
                {areas
                  .sort((a, b) => (a.Area > b.Area ? 1 : -1))
                  .map((a) => (
                    <option value={a.idArea}>{a.Area}</option>
                  ))}
              </Input>
            </Col>
          </Row>
          <br />

          {/* KPIs */}
          <Row style={{ justifyContent: "center" }}>
            <Col md={3}>
              <Card className="text-center" style={{ borderTop: "4px solid #1565c0" }}>
                <CardHeader tag="h6">Piezas vendidas</CardHeader>
                <CardBody><CardText tag="h3">{num(totales.cantidad)}</CardText></CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center" style={{ borderTop: "4px solid #1565c0" }}>
                <CardHeader tag="h6">Venta total</CardHeader>
                <CardBody><CardText tag="h3">{money(totales.venta)}</CardText></CardBody>
              </Card>
            </Col>
            {puedeVerCostos ? (
              <Col md={3}>
                <Card className="text-center" style={{ borderTop: "4px solid #2e7d32" }}>
                  <CardHeader tag="h6">Utilidad calculada</CardHeader>
                  <CardBody><CardText tag="h3" style={colorUtil(totales.utilidad)}>{money(totales.utilidad)}</CardText></CardBody>
                </Card>
              </Col>
            ) : null}
            {puedeVerCostos ? (
              <Col md={3}>
                <Card className="text-center" style={{ borderTop: "4px solid #2e7d32" }}>
                  <CardHeader tag="h6">Margen</CardHeader>
                  <CardBody><CardText tag="h3" style={colorUtil(totales.margen)}>{totales.margen.toFixed(1)}%</CardText></CardBody>
                </Card>
              </Col>
            ) : null}
          </Row>
          <br />

          {/* Resumen compacto por plataforma */}
          <h5 align="center">Venta total por plataforma</h5>
          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead className="thead-light">
              <tr>
                <th>Plataforma</th>
                <th>Cantidad de Articulos</th>
                <th>Venta (dinero)</th>
                {puedeVerCostos ? <th>Utilidad Calculada</th> : null}
                {puedeVerCostos ? <th>% Utilidad</th> : null}
              </tr>
            </thead>
            <tbody>
              {resumen
                .sort((a, b) => b.Venta - a.Venta)
                .map((a) => (
                  <tr>
                    <td><Badge color="info">{a.Area}</Badge></td>
                    <td>{num(a.Cantidad)}</td>
                    <td>{money(a.Venta)}</td>
                    {puedeVerCostos ? <td style={colorUtil(a.Utilidad)}>{money(a.Utilidad)}</td> : null}
                    {puedeVerCostos ? (
                      <td style={colorUtil(a.PorcentajeUtilidad)}>{a.PorcentajeUtilidad.toFixed(1)}%</td>
                    ) : null}
                  </tr>
                ))}
            </tbody>
          </Table>

          {/* Grafica */}
          <div
            id="chartdivPlataforma"
            style={{ width: "100%", height: "400px" }}
          ></div>
          <br />

          {/* Detalle por producto */}
          <h5 align="center">Detalle de venta por producto</h5>
          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tbody>
              {commentsData.map((a) => (
                <tr>
                  <td>{a.Codigo}</td>
                  <td>{a.Nombre}</td>
                  <td><Badge color="info">{a.Area}</Badge></td>
                  <td>{a.Linea}</td>
                  <td>{num(a.Cantidad)}</td>
                  <td>{money(a.Venta)}</td>
                  {puedeVerCostos ? (
                    <td style={colorUtil(a.Utilidad)}>{money(a.Utilidad)}</td>
                  ) : null}
                  {puedeVerCostos ? (
                    <td style={colorUtil(a.PorcentajeUtilidad)}>{(a.PorcentajeUtilidad * 100).toFixed(1)}%</td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}
      <br />
      {loader}
    </>
  );
}

export default ReporteVentasPlataforma;
